import React, { useState } from "react";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import OracleCandlestickChart from "@/components/predictor/OracleCandlestickChart";
import OracleScoreCard from "@/components/predictor/OracleScoreCard";
import OracleScannerPanel from "@/components/predictor/OracleScannerPanel";
import OracleCryptoIntelligence from "@/components/predictor/OracleCryptoIntelligence";
import Logix2RecommendationCard from "@/components/predictor/Logix2RecommendationCard";
import Logix2ReasoningPanel from "@/components/predictor/Logix2ReasoningPanel";
import Logix2IntelligenceConsole from "@/components/predictor/Logix2IntelligenceConsole";
import LockedFeature from "@/components/membership/LockedFeature";
import { useAuth } from "@/contexts/AuthContext";
// Phase 30D — Premium animated market card (additive)
import AnimatedMarketCard from "@/components/animation/AnimatedMarketCard";

/**
 * CareLogix™ ORACLE — Market Predictor Hub (Phase 27B).
 *
 * 10 predictor cards with a built-in expand-to-detail panel that includes the
 * Visual Charting Engine + AI Scoring Engine + Scanner Engine when relevant.
 * Wired into the public Oracle home page, additive (under modes bar).
 */

const PREDICTORS = [
  { id: "stock",      label: "ORACLE Stock Predictor",         icon: "📈", color: "#10b981", assetClass: "stocks",     defaultSymbol: "AAPL",     scannerType: "breakout_candidates", capability: ["chart", "score", "scanner"] },
  { id: "etf",        label: "ORACLE ETF Predictor",           icon: "📊", color: "#22d3ee", assetClass: "etfs",       defaultSymbol: "SPY",      scannerType: "breakout_candidates", capability: ["chart", "score"] },
  { id: "fx",         label: "ORACLE FX Pro",                  icon: "💱", color: "#67e8f9", assetClass: "forex",      defaultSymbol: "EUR/USD",  scannerType: "fx_volatility",       capability: ["chart", "score", "scanner"] },
  { id: "crypto",     label: "ORACLE Crypto Predictor",        icon: "₿",  color: "#fcd34d", assetClass: "crypto",     defaultSymbol: "BTC",      scannerType: "crypto_breakout",     capability: ["chart", "crypto", "score", "scanner"] },
  { id: "options",    label: "ORACLE Options Flow",            icon: "🧭", color: "#a78bfa", assetClass: "options",    defaultSymbol: "NVDA",     scannerType: "unusual_volume",      capability: ["chart", "score", "scanner"] },
  { id: "sports",     label: "ORACLE Sports Intel",            icon: "🏆", color: "#fb923c", assetClass: "sports",     defaultSymbol: "NBA · LAL vs BOS", scannerType: "sports_odds_movement", capability: ["score", "scanner"] },
  { id: "healthcare", label: "ORACLE Healthcare Market Intel", icon: "⚕️", color: "#fda4af", assetClass: "healthcare", defaultSymbol: "XLV",      scannerType: "fda_catalyst_plays",  capability: ["chart", "score", "scanner"] },
  { id: "startup",    label: "ORACLE Startup Radar",           icon: "🚀", color: "#c5a059", assetClass: "startups",   defaultSymbol: "BiomeChain", scannerType: "startup_funding_alerts", capability: ["score", "scanner"] },
  { id: "grant",      label: "ORACLE Grant Radar",             icon: "🎓", color: "#34d399", assetClass: "grants",     defaultSymbol: "NIH · SBIR Phase I", scannerType: "grant_opportunity_alerts", capability: ["scanner"] },
  { id: "africa",     label: "ORACLE Africa Investment Radar", icon: "🌍", color: "#f59e0b", assetClass: "african",    defaultSymbol: "NGX:DANGCEM", scannerType: "breakout_candidates", capability: ["score"] },
];

export default function MarketPredictorHub({ testid = "market-predictor-hub" }) {
  const [activeId, setActiveId] = useState("stock");
  const active = PREDICTORS.find((p) => p.id === activeId) || PREDICTORS[0];
  const can = (cap) => active.capability.includes(cap);
  const auth = useAuth?.() || {};
  const userName = auth?.user?.name || auth?.user?.email || null;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Market Predictor Hub™ · 10 Predictors</span>
          <h2 style={S.title}>✨ Choose a Predictor</h2>
        </div>
        <span style={S.badge} data-testid={`${testid}-policy`}>v27 · HITL required</span>
      </header>

      <ComplianceLock variant="banner" short policy="v27" testid={`${testid}-compliance-top`} />

      <div style={S.cardsRow} role="tablist" aria-label="Predictor categories">
        {PREDICTORS.map((p) => {
          const on = p.id === activeId;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setActiveId(p.id)}
              data-testid={`predictor-card-${p.id}`}
              style={{
                ...S.card,
                borderColor: on ? p.color : "rgba(255,255,255,0.08)",
                background: on ? `${p.color}1a` : "#040608",
              }}
            >
              <span style={{ ...S.cardIcon, color: p.color }}>{p.icon}</span>
              <span style={S.cardLabel}>{p.label}</span>
              <span style={S.cardMeta}>
                {p.capability.map((c) => (
                  <span key={c} style={S.capChip}>{c}</span>
                ))}
              </span>
            </button>
          );
        })}
      </div>

      <div style={S.detail} data-testid={`predictor-detail-${active.id}`}>
        <header style={S.detailHead}>
          <h3 style={{ ...S.title, color: active.color }}>
            <span aria-hidden="true">{active.icon}</span> {active.label} · <small style={{ color: "#94a3b8" }}>{active.defaultSymbol}</small>
          </h3>
          <span style={S.badge}>{active.assetClass.toUpperCase()}</span>
        </header>
        <div style={S.detailGrid}>
          {/* Phase 30D — Animated Market Card (preview, available to all tiers) */}
          <AnimatedMarketCard
            symbol={active.defaultSymbol.replace(/\s.*$/, "")}
            assetClass={active.assetClass}
            bias={active.assetClass === "sports" ? "watch" : "watch"}
            entry={[180, 184]}
            stop={[176]}
            target={[191, 198]}
            score={62}
            risk="medium"
            confidence={64}
            horizon="swing · 2–6d"
            testid={`predictor-${active.id}-animated-card`}
          />
          {can("chart") && (
            <LockedFeature featureKey="predictor_chart">
              <OracleCandlestickChart symbol={active.defaultSymbol.replace(/\s.*$/, "")} assetClass={active.assetClass} testid={`predictor-${active.id}-chart`} />
            </LockedFeature>
          )}
          {can("crypto") && (
            <LockedFeature featureKey="predictor_crypto_intel">
              <OracleCryptoIntelligence defaultSymbol={active.defaultSymbol} testid={`predictor-${active.id}-crypto`} />
            </LockedFeature>
          )}
          {can("score") && <OracleScoreCard symbol={active.defaultSymbol} assetClass={active.assetClass} testid={`predictor-${active.id}-score`} />}
          {can("score") && (
            <LockedFeature featureKey="predictor_logix2_reco">
              <Logix2RecommendationCard
                symbol={active.defaultSymbol}
                assetClass={active.assetClass}
                userName={userName}
                testid={`predictor-${active.id}-logix2`}
              />
            </LockedFeature>
          )}
          {can("score") && (
            <LockedFeature featureKey="predictor_logix2_reco">
              <Logix2ReasoningPanel
                symbol={active.defaultSymbol.replace(/\s.*$/, "")}
                assetClass={active.assetClass}
                scannerType={active.scannerType}
                isAdmin={!!auth?.isAdminOne}
                testid={`predictor-${active.id}-reasoning`}
              />
            </LockedFeature>
          )}
          {can("score") && (
            <LockedFeature featureKey="predictor_logix2_reco">
              <Logix2IntelligenceConsole
                symbol={active.defaultSymbol.replace(/\s.*$/, "")}
                assetClass={active.assetClass}
                scannerType={active.scannerType}
                isAdmin={!!auth?.isAdminOne}
                testid={`predictor-${active.id}-intel-console`}
              />
            </LockedFeature>
          )}
          {can("scanner") && (
            <LockedFeature featureKey="predictor_scanner_full">
              <OracleScannerPanel defaultType={active.scannerType} compact testid={`predictor-${active.id}-scanner`} />
            </LockedFeature>
          )}
        </div>
      </div>

      <ComplianceLock variant="footer" policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.92), rgba(11,15,20,0.96))", border: "1px solid rgba(197,160,89,0.20)", borderRadius: 16, padding: 16, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 18 },
  badge: { color: "#c5a059", border: "1px solid #c5a059", padding: "4px 10px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em", fontWeight: 800 },
  cardsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 8 },
  card: { appearance: "none", padding: 12, borderRadius: 12, border: "1px solid", cursor: "pointer", color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 6, textAlign: "left", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", minWidth: 0 },
  cardIcon: { fontSize: 24 },
  cardLabel: { color: "#fde68a", fontSize: 13, fontWeight: 700, lineHeight: 1.25 },
  cardMeta: { display: "flex", gap: 4, flexWrap: "wrap" },
  capChip: { color: "#94a3b8", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", padding: "2px 7px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.06em" },
  detail: { display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" },
  detailHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr", gap: 10, minWidth: 0 },
};
